import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { LIVE_READINGS_DISMISS_VIA } from '../../const';
import WaitlistApi from '../../service/Api/waitlistApi';
import { normalizeEmail } from '../../utils/helper';
import { setUserLiveReadings } from './authSlice';

/**
 * Live Readings waitlist (HORO-10471) — fake-door demand test.
 *
 * Nothing about an anonymous join is persisted on the device. Deduplication is
 * the server's job: the partial unique index on (tenant, email, source) means a
 * repeat submission updates the existing row instead of adding one, so a client
 * flag buys no data integrity — it only ever risked withholding the form from a
 * second person on a shared device, and losing their signal.
 *
 * `joined` therefore lives for the current page session only: it confirms the
 * submit you just made, and is gone on reload. Signed-in users are governed by
 * the account instead — see the selector at the bottom.
 */
const initialState = {
    joining: false,
    joined: false,
    error: null,
    // Set after a join that originated from the promo modal, so the chat page
    // knows to show the confirmation toast once the user is routed back.
    showJoinedToast: false
};

export const joinWaitlist = createAsyncThunk('waitlist/join', async (email, { dispatch, rejectWithValue }) => {
    try {
        const data = await WaitlistApi.join(normalizeEmail(email));
        const liveReadings = data?.data?.liveReadings;

        // Signed-in users get their account state refreshed in place; the
        // response carries `liveReadings` so no /user refetch is needed.
        if (liveReadings) {
            dispatch(setUserLiveReadings(liveReadings));
        }

        return true;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.error || 'unknown');
    }
});

export const dismissLiveReadingsPromo = createAsyncThunk('waitlist/dismissPromo', async (via, { dispatch, rejectWithValue }) => {
    try {
        const data = await WaitlistApi.dismissPromo(via || LIVE_READINGS_DISMISS_VIA.CLOSE);
        const liveReadings = data?.data?.liveReadings;

        if (liveReadings) {
            dispatch(setUserLiveReadings(liveReadings));
        }

        return true;
    } catch (error) {
        return rejectWithValue(error?.response?.data?.error || 'unknown');
    }
});

const waitlistSlice = createSlice({
    name: 'waitlist',
    initialState,
    reducers: {
        clearWaitlistError: (state) => {
            state.error = null;
        },
        setShowJoinedToast: (state, { payload }) => {
            state.showJoinedToast = !!payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(joinWaitlist.pending, (state) => {
                state.joining = true;
                state.error = null;
            })
            .addCase(joinWaitlist.fulfilled, (state) => {
                state.joining = false;
                // Session-scoped confirmation only — deliberately not persisted.
                state.joined = true;
            })
            .addCase(joinWaitlist.rejected, (state, { payload }) => {
                state.joining = false;
                state.error = payload;
            });
    }
});

export const { clearWaitlistError, setShowJoinedToast } = waitlistSlice.actions;

export const getWaitlistState = (state) => state.waitlist;
export const getShowJoinedToast = (state) => state.waitlist.showJoinedToast;

/**
 * Single source of truth for "should this person see the joined state".
 *
 * The authority depends on who's asking:
 *
 *   - Signed in  -> the ACCOUNT (`user.liveReadings.joinedWaitlistAt`), which
 *     survives a cleared cache and follows them across devices.
 *   - Anonymous  -> whether they joined during THIS page session. Nothing is
 *     persisted, so returning later shows the form again. That's intentional:
 *     the device can't tell one visitor from another, and withholding the form
 *     from a second person on a shared machine would silently drop the exact
 *     signal this test exists to collect. Re-submitting costs nothing — the
 *     server dedupes on the address.
 *
 * The signed-in branch deliberately does NOT fall back to
 * `state.waitlist.joined`: an anonymous join earlier in the same session would
 * otherwise mark the account as joined, when it may well have used a different
 * address than the one on the account.
 */
export const getIsOnWaitlist = (state) => {
    const isUser = !!state.auth.user?.id;

    if (isUser) {
        return !!state.auth.user?.liveReadings?.joinedWaitlistAt;
    }

    return state.waitlist.joined;
};

export const waitlistReducer = waitlistSlice.reducer;

export default waitlistSlice.reducer;
